import React, { useEffect, useRef, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Heart, MessageSquare, DollarSign, Share2, X, Volume2, VolumeX, Play, ChevronUp } from "lucide-react";
import Hls from "hls.js";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { tapHaptic } from "../lib/haptics";
import ShareMenu from "../components/ShareMenu";
import WatchingWithYou from "../components/WatchingWithYou";
import LiveChatOverlay from "../components/LiveChatOverlay";

// ─────────────────────────────────────────────────────────────────────────────
// ForYouLiveFeed — vertical, snap-scroll feed of LIVE streams.
// Portrait-first (9:16), one card per viewport. Only the visible card plays;
// the two adjacent cards preload but stay paused. Mirrors the TikTok / IG
// Reels UX so viewers can thumb-scroll through the platform's live catalog.
// ─────────────────────────────────────────────────────────────────────────────

function LiveCard({ stream, active, muted, onToggleMute, onOpenTip, onOpenShare, isAuthed }) {
  const videoRef = useRef(null);
  const hlsRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [likeCount, setLikeCount] = useState(stream.like_count || 0);
  const [liked, setLiked] = useState(false);
  const [chat, setChat] = useState([]);
  const src = stream.hls_url || stream.stream_url || "";

  // Poll chat only for the ACTIVE card — no reason to burn requests on
  // cards buried offscreen. Adds a 5s interval refresh which is a good
  // balance between "feels live" and "doesn't hammer the server".
  useEffect(() => {
    if (!active || !stream?.id) { setChat([]); return undefined; }
    let cancelled = false;
    const fetchChat = async () => {
      try {
        const r = await api.get(`/live/${stream.id}/chat?limit=30`);
        if (cancelled) return;
        setChat(Array.isArray(r.data) ? r.data : []);
      } catch { /* transient */ }
    };
    fetchChat();
    const id = setInterval(fetchChat, 5000);
    return () => { cancelled = true; clearInterval(id); };
  }, [active, stream?.id]);

  // Attach / detach source per active state. Non-active cards keep the
  // <video> element around but destroy their HLS instance so we don't burn
  // bandwidth on 5 concurrent live streams.
  useEffect(() => {
    const el = videoRef.current;
    if (!el) return undefined;
    let cancelled = false;
    const detach = () => {
      try { hlsRef.current?.destroy(); } catch { /* fine */ }
      hlsRef.current = null;
      try { el.pause(); } catch { /* fine */ }
      try { el.removeAttribute("src"); el.load(); } catch { /* fine */ }
    };
    if (!active || !src) { detach(); setPlaying(false); return undefined; }
    const isHls = /\.m3u8($|\?)/i.test(src);
    if (isHls && Hls.isSupported()) {
      const hls = new Hls({ maxBufferLength: 10, enableWorker: true, lowLatencyMode: true });
      hls.loadSource(src);
      hls.attachMedia(el);
      hlsRef.current = hls;
      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        if (cancelled) return;
        el.play().then(() => setPlaying(true)).catch(() => setPlaying(false));
      });
      hls.on(Hls.Events.ERROR, (_, data) => {
        if (data?.fatal) detach();
      });
    } else {
      el.src = src;
      el.play().then(() => setPlaying(true)).catch(() => setPlaying(false));
    }
    return () => { cancelled = true; detach(); };
  }, [active, src]);

  // Keep the muted attribute in sync — required so browsers don't kill
  // autoplay on a policy-change tick.
  useEffect(() => { if (videoRef.current) videoRef.current.muted = muted; }, [muted]);

  const handleLike = () => {
    tapHaptic();
    setLiked((v) => !v);
    setLikeCount((n) => (liked ? Math.max(0, n - 1) : n + 1));
    // Backend has no per-stream "like" endpoint yet — this is a purely
    // client-side heart tap for now. Tracked in backlog for iter210+ so
    // creators can see hearts land in their stream analytics.
  };

  const displayName =
    stream.host_display_name || stream.creator_name || stream.host || "Creator";
  const avatar =
    stream.host_avatar_url ||
    `https://api.dicebear.com/7.x/thumbs/svg?seed=${encodeURIComponent(displayName)}`;

  return (
    <div
      data-testid="feed-live-card"
      className="snap-start relative h-[100dvh] w-full flex items-center justify-center bg-black overflow-hidden"
    >
      {/* Video / thumbnail */}
      {src ? (
        <video
          ref={videoRef}
          data-testid="feed-live-video"
          className="absolute inset-0 w-full h-full object-cover"
          playsInline
          autoPlay
          muted={muted}
          loop
          onClick={onToggleMute}
        />
      ) : (
        <img
          src={stream.thumbnail_url || stream.cover_url || "/tapintv-hero-banner.png"}
          alt={stream.title}
          className="absolute inset-0 w-full h-full object-cover"
        />
      )}

      {/* Bottom gradient for text legibility */}
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-2/5 bg-gradient-to-t from-black via-black/60 to-transparent" />
      {/* Top gradient for the LIVE pill legibility */}
      <div className="pointer-events-none absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-black/80 to-transparent" />

      {/* LIVE badge + viewer count + watching-with-you strip (top-left) */}
      <div className="absolute top-4 left-4 z-10 flex flex-col items-start gap-1.5">
        <div className="flex items-center gap-2">
          <span
            data-testid="feed-live-badge"
            className="inline-flex items-center gap-1.5 rounded-md bg-red-600 px-2 py-0.5 text-[10px] font-black uppercase tracking-widest text-white"
          >
            <span className="h-1.5 w-1.5 rounded-full bg-white animate-pulse" />
            Live
          </span>
          {typeof stream.viewer_count === "number" && (
            <span className="rounded-md bg-black/60 backdrop-blur px-2 py-0.5 text-[11px] font-semibold text-white">
              {compactNumber(stream.viewer_count)} watching
            </span>
          )}
          {!playing && active && src && (
            <span className="rounded-md bg-black/60 px-2 py-0.5 text-[10px] font-semibold text-white/80">
              Loading…
            </span>
          )}
        </div>
        {active && (
          <WatchingWithYou streamId={stream.id} isAuthed={isAuthed} />
        )}
      </div>

      {/* TikTok-Live style floating chat overlay */}
      {active && (
        <LiveChatOverlay
          streamId={stream.id}
          messages={chat}
          isAuthed={isAuthed}
          className="left-3 right-24"
        />
      )}

      {/* Right action rail (heart / comment / tip / share) */}
      <div className="absolute right-3 bottom-24 z-10 flex flex-col items-center gap-5">
        <button
          data-testid="feed-action-like"
          onClick={handleLike}
          className="flex flex-col items-center gap-1 text-white active:scale-95 transition-transform"
        >
          <span className="h-11 w-11 rounded-full bg-white/15 backdrop-blur flex items-center justify-center">
            <Heart className={`h-6 w-6 ${liked ? "fill-red-500 text-red-500" : ""}`} />
          </span>
          <span className="text-[11px] font-semibold drop-shadow">{compactNumber(likeCount)}</span>
        </button>

        <Link
          to={`/live/${stream.id}`}
          data-testid="feed-action-comment"
          onClick={() => tapHaptic()}
          className="flex flex-col items-center gap-1 text-white active:scale-95 transition-transform"
        >
          <span className="h-11 w-11 rounded-full bg-white/15 backdrop-blur flex items-center justify-center">
            <MessageSquare className="h-6 w-6" />
          </span>
          <span className="text-[11px] font-semibold drop-shadow">Chat</span>
        </Link>

        <button
          data-testid="feed-action-tip"
          onClick={() => { tapHaptic(); onOpenTip(stream); }}
          className="flex flex-col items-center gap-1 text-white active:scale-95 transition-transform"
        >
          <span className="h-11 w-11 rounded-full bg-yellow-400/90 text-black flex items-center justify-center">
            <DollarSign className="h-6 w-6" />
          </span>
          <span className="text-[11px] font-semibold drop-shadow">Tip</span>
        </button>

        <button
          data-testid="feed-action-share"
          onClick={() => { tapHaptic(); onOpenShare(stream); }}
          className="flex flex-col items-center gap-1 text-white active:scale-95 transition-transform"
        >
          <span className="h-11 w-11 rounded-full bg-white/15 backdrop-blur flex items-center justify-center">
            <Share2 className="h-6 w-6" />
          </span>
          <span className="text-[11px] font-semibold drop-shadow">Share</span>
        </button>

        <button
          data-testid="feed-action-mute"
          onClick={onToggleMute}
          className="flex flex-col items-center gap-1 text-white/80 active:scale-95 transition-transform"
        >
          <span className="h-9 w-9 rounded-full bg-white/10 backdrop-blur flex items-center justify-center">
            {muted ? <VolumeX className="h-5 w-5" /> : <Volume2 className="h-5 w-5" />}
          </span>
        </button>
      </div>

      {/* Bottom-left creator strip */}
      <div className="absolute left-4 right-24 bottom-6 z-10 text-white">
        <Link to={`/creator/${stream.host_id || stream.creator_id || ""}`} className="flex items-center gap-2 mb-2">
          <img
            src={avatar}
            alt={displayName}
            className="h-9 w-9 rounded-full border-2 border-white/90 object-cover bg-neutral-800"
            onError={(e) => { e.currentTarget.src = `https://api.dicebear.com/7.x/thumbs/svg?seed=${encodeURIComponent(displayName)}`; }}
          />
          <span className="font-black text-sm drop-shadow">@{displayName}</span>
        </Link>
        <div className="font-bold text-[15px] leading-tight drop-shadow line-clamp-2">
          {stream.title || "Live now"}
        </div>
        {(stream.category || stream.tags?.length) && (
          <div className="mt-1 flex flex-wrap gap-1.5">
            {stream.category && (
              <span className="rounded-full bg-white/15 backdrop-blur px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider">
                #{stream.category}
              </span>
            )}
          </div>
        )}
        <Link
          to={`/live/${stream.id}`}
          data-testid="feed-open-full"
          className="mt-3 inline-flex items-center gap-1 rounded-full bg-yellow-400 px-3 py-1.5 text-[12px] font-black text-black uppercase tracking-wider active:scale-95 transition-transform"
        >
          Open full player
          <ChevronUp className="h-3.5 w-3.5 rotate-90" />
        </Link>
      </div>

      {/* Center tap-to-play overlay when paused (autoplay blocked) */}
      {active && !playing && src && (
        <button
          data-testid="feed-tap-play"
          onClick={() => videoRef.current?.play().then(() => setPlaying(true)).catch(() => {})}
          className="absolute inset-0 z-0 flex items-center justify-center"
        >
          <span className="h-16 w-16 rounded-full bg-white/25 backdrop-blur flex items-center justify-center">
            <Play className="h-8 w-8 text-white fill-white" />
          </span>
        </button>
      )}
    </div>
  );
}

// TipSheet — thin bottom sheet with quick preset amounts. Uses the
// wallet-based instant tip endpoint (`POST /api/funds/tip`) so a viewer
// can send $1–$50 without leaving the feed. No Stripe round-trip.
function TipSheet({ stream, onClose }) {
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();
  const presets = [1, 5, 10, 20, 50];

  const send = async (amount) => {
    if (!user) { onClose(); nav("/login"); return; }
    const creatorId = stream.creator_id || stream.host_id;
    if (!creatorId) {
      toast.error("Can't tip — creator info missing");
      return;
    }
    setBusy(true);
    try {
      // Instant wallet-based tip (no Stripe round-trip). The wallet
      // router is mounted at `/api/funds`, so the tip endpoint lives
      // at `/api/funds/tip` — not `/api/tip`. Honours the existing PPV
      // split (default 70/30). If balance is short, the backend returns
      // 400 and we bounce the user to /wallet to top up.
      await api.post("/funds/tip", { creator_id: creatorId, amount });
      toast.success(`Sent $${amount} tip to @${stream.host_display_name || "creator"} — thanks for supporting!`);
      onClose();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Tip failed — top up your wallet";
      if (String(msg).toLowerCase().includes("balance") || String(msg).toLowerCase().includes("insufficient")) {
        onClose();
        nav("/wallet");
      } else {
        toast.error(msg);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-end" data-testid="feed-tip-sheet">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative w-full bg-neutral-950 border-t border-yellow-400/40 rounded-t-2xl p-5 pb-8">
        <div className="mx-auto h-1 w-10 rounded-full bg-white/20 mb-4" />
        <div className="flex items-center justify-between mb-1">
          <div className="text-white text-lg font-black">Tip @{stream.host_display_name || "creator"}</div>
          <button onClick={onClose} className="text-white/60"><X className="h-5 w-5" /></button>
        </div>
        <div className="text-white/60 text-sm mb-4">Show the creator some love — instant on-stream celebration.</div>
        <div className="grid grid-cols-5 gap-2">
          {presets.map((amt) => (
            <button
              key={amt}
              data-testid={`feed-tip-${amt}`}
              disabled={busy}
              onClick={() => send(amt)}
              className="rounded-xl bg-yellow-400 text-black font-black py-3 active:scale-95 transition-transform disabled:opacity-40"
            >
              ${amt}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function ForYouLiveFeed() {
  const { user } = useAuth();
  const [streams, setStreams] = useState(null);
  const [active, setActive] = useState(0);
  const [muted, setMuted] = useState(true);
  const [tipStream, setTipStream] = useState(null);
  const [shareStream, setShareStream] = useState(null);
  const containerRef = useRef(null);
  const cardRefs = useRef([]);
  const nav = useNavigate();

  useEffect(() => {
    api.get("/live").then((r) => {
      const list = Array.isArray(r.data) ? r.data : [];
      // Prefer streams with an active HLS URL; empty state handled below.
      list.sort((a, b) => (b.viewer_count || 0) - (a.viewer_count || 0));
      setStreams(list);
    }).catch(() => setStreams([]));
  }, []);

  // IntersectionObserver — mark the card most in-view as active. Handles
  // both smooth-scroll and thumb-fling correctly (unlike wheel/scroll math).
  useEffect(() => {
    if (!streams || streams.length === 0) return undefined;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting && e.intersectionRatio > 0.6) {
            const idx = Number(e.target.getAttribute("data-idx"));
            if (!Number.isNaN(idx)) setActive(idx);
          }
        });
      },
      { threshold: [0.6, 0.9] }
    );
    cardRefs.current.forEach((el) => el && io.observe(el));
    return () => io.disconnect();
  }, [streams]);

  const toggleMute = useCallback(() => setMuted((v) => !v), []);

  if (streams === null) {
    return (
      <div className="fixed inset-0 bg-black flex items-center justify-center z-[60]">
        <div className="text-white/70 text-sm">Loading the feed…</div>
      </div>
    );
  }

  if (streams.length === 0) {
    return (
      <div className="fixed inset-0 bg-black flex flex-col items-center justify-center z-[60] px-6 text-center">
        <div className="text-yellow-400 text-4xl mb-2">🎬</div>
        <div className="text-white text-xl font-black">Nobody&apos;s live right now</div>
        <div className="text-white/60 text-sm mt-2">Follow your favourite creators — you&apos;ll get pushed here the moment they go live.</div>
        <Link to="/browse/all" className="mt-6 rounded-full bg-yellow-400 text-black font-black px-5 py-2.5">
          Browse channels
        </Link>
        <button onClick={() => nav(-1)} className="mt-3 text-white/60 text-sm underline">Go back</button>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black z-[60]" data-testid="for-you-live-feed">
      {/* Top-left close */}
      <button
        data-testid="feed-close"
        onClick={() => nav("/")}
        className="absolute top-4 right-4 z-30 h-9 w-9 rounded-full bg-black/60 backdrop-blur flex items-center justify-center text-white"
      >
        <X className="h-5 w-5" />
      </button>

      {/* Feed heading (fades on scroll) */}
      <div className="absolute top-4 left-1/2 -translate-x-1/2 z-20 pointer-events-none">
        <div className="text-white/90 font-black text-sm tracking-widest uppercase drop-shadow">For You · Live</div>
      </div>

      <div
        ref={containerRef}
        className="h-[100dvh] w-full overflow-y-auto snap-y snap-mandatory scroll-smooth"
        style={{ scrollSnapType: "y mandatory" }}
      >
        {streams.map((s, i) => (
          <div
            key={s.id}
            data-idx={i}
            ref={(el) => (cardRefs.current[i] = el)}
          >
            <LiveCard
              stream={s}
              active={i === active}
              muted={muted}
              onToggleMute={toggleMute}
              onOpenTip={setTipStream}
              onOpenShare={setShareStream}
              isAuthed={!!user}
            />
          </div>
        ))}
      </div>

      {tipStream && (
        <TipSheet stream={tipStream} onClose={() => setTipStream(null)} />
      )}
      {shareStream && (
        <div className="fixed inset-0 z-[70] flex items-end" data-testid="feed-share-sheet">
          <div className="absolute inset-0 bg-black/60" onClick={() => setShareStream(null)} />
          <div className="relative w-full bg-neutral-950 border-t border-yellow-400/40 rounded-t-2xl p-5 pb-8">
            <div className="mx-auto h-1 w-10 rounded-full bg-white/20 mb-4" />
            <div className="flex items-center justify-between mb-3">
              <div className="text-white text-lg font-black">Share this stream</div>
              <button onClick={() => setShareStream(null)} className="text-white/60"><X className="h-5 w-5" /></button>
            </div>
            <ShareMenu
              url={`${window.location.origin}/live/${shareStream.id}`}
              title={`${shareStream.host_display_name || "A creator"} is live on Tap In TV — join now`}
              text={shareStream.title || ""}
              testidPrefix="feed-share"
            />
          </div>
        </div>
      )}
    </div>
  );
}

function compactNumber(n) {
  if (n === null || n === undefined) return "0";
  if (n < 1000) return String(n);
  if (n < 10000) return `${(n / 1000).toFixed(1).replace(/\.0$/, "")}K`;
  if (n < 1000000) return `${Math.round(n / 1000)}K`;
  return `${(n / 1000000).toFixed(1).replace(/\.0$/, "")}M`;
}
